(() => {
  if (!customElements.get('shipping-count-down')) {

    function tzParts (date, timeZone) {
      const dtf = new Intl.DateTimeFormat('en-US', {
        timeZone, hour12: false,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      });
      const bag = {};
      for (const p of dtf.formatToParts(date)) bag[p.type] = p.value;
      return { y: +bag.year, m: +bag.month - 1, d: +bag.day, H: +bag.hour, M: +bag.minute, S: +bag.second };
    }

    function tzOffsetMinutesAt (timeZone, utcDate) {
      const parts = tzParts(utcDate, timeZone);
      const localAsUTC = Date.UTC(parts.y, parts.m, parts.d, parts.H, parts.M, parts.S);
      return Math.round((localAsUTC - utcDate.getTime()) / 60000);
    }

    function cutoffUtcMs (timeZone, y, m, d, cutoffHour24) {
      const noonUTC = new Date(Date.UTC(y, m, d, 12, 0, 0));
      const offsetMin = tzOffsetMinutesAt(timeZone, noonUTC);
      return Date.UTC(y, m, d, cutoffHour24, 0, 0) - offsetMin * 60000;
    }

    function weekdayInTZ (timeZone, y, m, d) {
      const parts = tzParts(new Date(Date.UTC(y, m, d, 12, 0, 0)), timeZone);
      return new Date(Date.UTC(parts.y, parts.m, parts.d, 12, 0, 0)).getUTCDay();
    }

    function isWeekendInTZ (timeZone, y, m, d) {
      const dow = weekdayInTZ(timeZone, y, m, d);
      return dow === 0 || dow === 6;
    }

    function nextDayInTZ (y, m, d, timeZone) {
      const noonUTC = new Date(Date.UTC(y, m, d, 12, 0, 0));
      noonUTC.setUTCDate(noonUTC.getUTCDate() + 1);
      const p = tzParts(noonUTC, timeZone);
      return { y: p.y, m: p.m, d: p.d };
    }

    function nextBusinessDayInTZ (timeZone, y, m, d) {
      let cur = { y, m, d };
      while (isWeekendInTZ(timeZone, cur.y, cur.m, cur.d)) {
        cur = nextDayInTZ(cur.y, cur.m, cur.d, timeZone);
      }
      return cur;
    }

    class ShippingCountDown extends HTMLElement {
      constructor () {
        super();
        this.timerInterval = null;
        this.hasDisplayed = false;
        this.rawHTML = '';
      }

      connectedCallback () {
        const inner = this.querySelector('.shipping-count-down-content');
        this.rawHTML = inner ? inner.innerHTML.trim() : '';
        this.render();
        this.startTimer();
      }

      disconnectedCallback () {
        clearInterval(this.timerInterval);
      }

      startTimer () {
        this.timerInterval = setInterval(() => this.render(), 1000);
      }

      render () {
        const { timerText, shipDateText } = this.getTimeData();
        const timerRegex = /\[\s*timer\s*\]/gi;
        const dateRegex = /\[\s*date\s*\]/gi;
        const updated = (this.rawHTML || '').replace(timerRegex, timerText).replace(dateRegex, shipDateText);
        this.innerHTML = `<div class="shipping-count-down-content">${updated}</div>`;
        if (!this.hasDisplayed) {
          this.style.opacity = '1';
          this.hasDisplayed = true;
        }
      }

      getTimeData () {
        const now = new Date();

        const cutoffAttr = (this.getAttribute('data-cutoff') || '5pm').trim().toLowerCase();
        const cutoffHourET = this.parseCutoffTo24h(cutoffAttr);
        const TZ = (this.getAttribute('data-cutoff-tz') || 'America/New_York').trim();
        const SKIP_WKND = this.hasAttribute('data-skip-weekend');

        const etNow = tzParts(now, TZ);

        let shipY = etNow.y, shipM = etNow.m, shipD = etNow.d;
        if (SKIP_WKND && isWeekendInTZ(TZ, shipY, shipM, shipD)) {
          ({ y: shipY, m: shipM, d: shipD } = nextBusinessDayInTZ(TZ, shipY, shipM, shipD));
        }
        let targetCutoffUTCms = cutoffUtcMs(TZ, shipY, shipM, shipD, cutoffHourET);
        const nowUTCms = now.getTime();

        if (nowUTCms >= targetCutoffUTCms) {
          let next = nextDayInTZ(shipY, shipM, shipD, TZ);
          if (SKIP_WKND) {
            next = nextBusinessDayInTZ(TZ, next.y, next.m, next.d);
          }
          shipY = next.y; shipM = next.m; shipD = next.d;
          targetCutoffUTCms = cutoffUtcMs(TZ, shipY, shipM, shipD, cutoffHourET);
        }

        const diffMs = Math.max(0, targetCutoffUTCms - nowUTCms);
        const hours = Math.floor(diffMs / 3_600_000);
        const minutes = Math.floor((diffMs % 3_600_000) / 60_000);
        const seconds = Math.floor((diffMs % 60_000) / 1000);
        const timerText = `${hours} hours ${minutes} minutes ${seconds} seconds`;

        const shipDate = new Date(Date.UTC(shipY, shipM, shipD, 12, 0, 0));
        const shipDateText = shipDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });

        return { timerText, shipDateText };
      }

      parseCutoffTo24h (value) {
        const trimmed = String(value || '').trim().toLowerCase();
        const ampm = trimmed.match(/^(\d{1,2})(am|pm)$/);
        if (ampm) {
          let [, hStr, mer] = ampm;
          let h = parseInt(hStr, 10) % 12;
          return mer === 'pm' ? h + 12 : h;
        }
        const justNum = trimmed.match(/^(\d{1,2})$/);
        if (justNum) {
          const h = Math.max(0, Math.min(23, parseInt(justNum[1], 10)));
          return h;
        }
        return 17;
      }
    }

    customElements.define('shipping-count-down', ShippingCountDown);
  }
})();